import { isProduction } from "../utils/constants";
import { bugsnag_log } from "../utils/bugsnag";

const inFlightChartBars = new Map();
const inFlightChartBarSnapshots = new Map();
const inFlightChartBarPages = new Map();
const completedChartBars = new Map();
const completedChartBarSnapshots = new Map();
const completedChartTradingMetadata = new Map();

export const CHART_BARS_CACHE_MAX_ENTRIES = 24;
export const CHART_BARS_FIRST_PAINT_MAX_AGE_MS = 5 * 60 * 1000;

const SUPPORTED_SCALES = new Set([
  "15s",
  "30s",
  "1m",
  "2m",
  "3m",
  "5m",
  "10m",
  "15m",
  "30m",
  "1h",
  "2h",
  "3h",
  "4h",
  "1d",
  "1w",
  "1mo",
]);

const normalizeScale = (s) => {
  const normalized = ({
  // legacy aliases
  "1day": "1d",
  "1min": "1m",
  "2min": "2m",
  "3min": "3m",
  "5min": "5m",

  // canonical
  "15s": "15s",
  "30s": "30s",
  "1m": "1m",
  "2m": "2m",
  "3m": "3m",
  "5m": "5m",
  "10m": "10m",
  "15m": "15m",
  "30m": "30m",
  "1h": "1h",
  "2h": "2h",
  "3h": "3h",
  "4h": "4h",
  "1d": "1d",
  "1w": "1w",
  "1mo": "1mo",
  }[s] || s);

  return SUPPORTED_SCALES.has(normalized) ? normalized : "1d";
};

const chartBarsRequest = ({
  tickerId,
  scale,
  from = null,
  to = null,
  limit = 1000,
  settleRecent = false,
}) => ({
  tickerId: String(tickerId),
  scale: normalizeScale(scale),
  from: from || null,
  to: to || null,
  limit,
  settleRecent: Boolean(settleRecent),
});

const chartBarsRequestKey = (request) => JSON.stringify(request);

const rememberChartBars = (requestKey, bars, fetchedAt = Date.now()) => {
  if (!Array.isArray(bars) || bars.length < 2) return;

  completedChartBars.delete(requestKey);
  completedChartBars.set(requestKey, { bars, fetchedAt });
  while (completedChartBars.size > CHART_BARS_CACHE_MAX_ENTRIES) {
    const oldestKey = completedChartBars.keys().next().value;
    completedChartBars.delete(oldestKey);
  }
};

const rememberChartBarSnapshot = (requestKey, bars, fetchedAt = Date.now()) => {
  if (!Array.isArray(bars) || bars.length < 2) return;

  completedChartBarSnapshots.delete(requestKey);
  completedChartBarSnapshots.set(requestKey, { bars, fetchedAt });
  while (completedChartBarSnapshots.size > CHART_BARS_CACHE_MAX_ENTRIES) {
    const oldestKey = completedChartBarSnapshots.keys().next().value;
    completedChartBarSnapshots.delete(oldestKey);
  }
};

export const getCachedChartBars = (
  request,
  {
    maxAgeMs = CHART_BARS_FIRST_PAINT_MAX_AGE_MS,
    now = Date.now(),
  } = {},
) => {
  const requestKey = chartBarsRequestKey(chartBarsRequest(request));
  const cached = completedChartBars.get(requestKey);
  if (!cached || now - cached.fetchedAt > maxAgeMs) return null;

  completedChartBars.delete(requestKey);
  completedChartBars.set(requestKey, cached);
  return cached.bars;
};

export const clearChartBarsCache = () => {
  completedChartBars.clear();
  completedChartBarSnapshots.clear();
};

const getChartBars = async ({ tickerId, scale, from = null, to = null, limit = 1000, settleRecent = false }) => {
  const normalizedRequest = chartBarsRequest({
    tickerId,
    scale,
    from,
    to,
    limit,
    settleRecent,
  });
  const requestKey = chartBarsRequestKey(normalizedRequest);

  if (inFlightChartBars.has(requestKey)) {
    return inFlightChartBars.get(requestKey);
  }

  const body = JSON.stringify({
    query: `
      query ChartBars($tickerId: ID!, $scale: String!, $from: ISO8601DateTime, $to: ISO8601DateTime, $limit: Int, $settleRecent: Boolean) {
        chartBars(tickerId: $tickerId, scale: $scale, from: $from, to: $to, limit: $limit, settleRecent: $settleRecent) {
          datetime
          open
          high
          low
          close
          volume
          vwap
        }
      }`,
    variables: {
      tickerId,
      scale: normalizedRequest.scale,
      from,
      to,
      limit,
      settleRecent: normalizedRequest.settleRecent,
    },
  });

  const request = (async () => {
    try {
      const res = await fetch("/graphql", { method: "POST", headers: { "Content-Type": "application/json" }, body });
      const json = await res.json();
      if (json.errors?.length) throw new Error(json.errors[0].message);
      const bars = json.data.chartBars;
      rememberChartBars(requestKey, bars);
      return bars;
    } catch (err) {
      if (!isProduction) console.error("getChartBars error:", err);
      bugsnag_log(err);
      throw err;
    } finally {
      inFlightChartBars.delete(requestKey);
    }
  })();

  inFlightChartBars.set(requestKey, request);
  return request;
};

export const getChartBarSnapshot = async ({
  tickerId,
  scale,
  from = null,
  to = null,
  limit = 1000,
}) => {
  const normalizedRequest = chartBarsRequest({
    tickerId,
    scale,
    from,
    to,
    limit,
  });
  const requestKey = chartBarsRequestKey(normalizedRequest);
  if (inFlightChartBarSnapshots.has(requestKey)) {
    return inFlightChartBarSnapshots.get(requestKey);
  }

  const body = JSON.stringify({
    query: `
      query ChartBarSnapshot($tickerId: ID!, $scale: String!, $from: ISO8601DateTime, $to: ISO8601DateTime, $limit: Int) {
        chartBarSnapshot(tickerId: $tickerId, scale: $scale, from: $from, to: $to, limit: $limit) {
          datetime
          open
          high
          low
          close
          volume
          vwap
        }
      }`,
    variables: {
      tickerId,
      scale: normalizedRequest.scale,
      from,
      to,
      limit,
    },
  });

  const request = (async () => {
    try {
      const response = await fetch("/graphql", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body,
      });
      const json = await response.json();
      if (json.errors?.length) throw new Error(json.errors[0].message);
      const bars = Array.isArray(json.data?.chartBarSnapshot)
        ? json.data.chartBarSnapshot
        : [];
      rememberChartBarSnapshot(requestKey, bars);
      return bars;
    } catch (error) {
      if (!isProduction) console.error("getChartBarSnapshot error:", error);
      bugsnag_log(error);
      throw error;
    } finally {
      inFlightChartBarSnapshots.delete(requestKey);
    }
  })();

  inFlightChartBarSnapshots.set(requestKey, request);
  return request;
};

export const getCachedChartBarSnapshot = (
  request,
  {
    maxAgeMs = CHART_BARS_FIRST_PAINT_MAX_AGE_MS,
    now = Date.now(),
  } = {},
) => {
  const requestKey = chartBarsRequestKey(chartBarsRequest(request));
  const cached = completedChartBarSnapshots.get(requestKey);
  if (!cached || now - cached.fetchedAt > maxAgeMs) return null;

  completedChartBarSnapshots.delete(requestKey);
  completedChartBarSnapshots.set(requestKey, cached);
  return cached.bars;
};

export const prefetchChartBarSnapshot = (request) => getChartBarSnapshot(request);

const getChartBarPage = async ({ tickerId, scale, before = null, limit = 500 }) => {
  const normalizedScale = normalizeScale(scale);
  const requestKey = JSON.stringify({
    tickerId: String(tickerId),
    scale: normalizedScale,
    before: before || null,
    limit,
  });

  if (inFlightChartBarPages.has(requestKey)) {
    return inFlightChartBarPages.get(requestKey);
  }

  const body = JSON.stringify({
    query: `
      query ChartBarPage($tickerId: ID!, $scale: String!, $before: ISO8601DateTime, $limit: Int) {
        chartBarPage(tickerId: $tickerId, scale: $scale, before: $before, limit: $limit) {
          bars {
            datetime
            open
            high
            low
            close
            volume
            vwap
          }
          hasMore
          nextCursor
          historyEnabled
          source
        }
      }`,
    variables: { tickerId, scale: normalizedScale, before, limit },
  });

  const request = (async () => {
    try {
      const res = await fetch("/graphql", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body,
      });
      const json = await res.json();
      if (json.errors?.length) throw new Error(json.errors[0].message);
      const page = json.data?.chartBarPage || {};
      return {
        bars: Array.isArray(page.bars) ? page.bars : [],
        hasMore: Boolean(page.hasMore),
        nextCursor: page.nextCursor || null,
        historyEnabled: Boolean(page.historyEnabled),
        source: page.source || "unavailable",
      };
    } catch (err) {
      if (!isProduction) console.error("getChartBarPage error:", err);
      bugsnag_log(err);
      throw err;
    } finally {
      inFlightChartBarPages.delete(requestKey);
    }
  })();

  inFlightChartBarPages.set(requestKey, request);
  return request;
};

const searchChartAssets = async (searchTerm, { signal } = {}) => {
  const normalizedSearchTerm = String(searchTerm || "").trim();
  if (!normalizedSearchTerm) return [];

  const body = JSON.stringify({
    query: `
      query SearchChartAssets($searchTerm: String!) {
        searchTickers(searchTerm: $searchTerm) {
          id
          ticker
          name
          assetType
          exchange
        }
      }`,
    variables: { searchTerm: normalizedSearchTerm },
  });

  const res = await fetch("/graphql", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body,
    signal,
  });
  const json = await res.json();
  if (!res.ok) throw new Error(`Asset search failed with status ${res.status}`);
  if (json.errors?.length) throw new Error(json.errors[0].message);

  return Array.isArray(json.data?.searchTickers)
    ? json.data.searchTickers
    : [];
};

// Entitlement-gated chart fields return no bars rather than an error. Ask
// only after an empty series; access can change, so this is never cached.
const getChartAvailability = async ({ tickerId }) => {
  const body = JSON.stringify({
    query: `
      query ChartAvailability($tickerId: ID!) {
        chartAvailability(tickerId: $tickerId) {
          tickerId
          assetType
          status
          eligible
          reason
          requiredTier
          upgradeRoute
        }
      }`,
    variables: { tickerId: String(tickerId) },
  });
  const res = await fetch("/graphql", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body,
  });
  const json = await res.json();
  if (!res.ok) throw new Error(`Chart availability failed with status ${res.status}`);
  if (json.errors?.length) throw new Error(json.errors[0].message);

  return json.data?.chartAvailability || null;
};

const getChartTradingMetadata = async (tickerId, { signal } = {}) => {
  const key = String(tickerId || "").trim();
  if (!key) return null;
  if (completedChartTradingMetadata.has(key)) {
    return completedChartTradingMetadata.get(key);
  }

  const body = JSON.stringify({
    query: `
      query ChartTradingMetadata($tickerId: ID!) {
        chartTradingMetadata(tickerId: $tickerId) {
          tickerId
          currency
          pointValue
          pointValueSource
          tickSize
        }
      }`,
    variables: { tickerId: key },
  });
  const res = await fetch("/graphql", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body,
    signal,
  });
  const json = await res.json();
  if (!res.ok) throw new Error(`Chart trading metadata failed with status ${res.status}`);
  if (json.errors?.length) throw new Error(json.errors[0].message);

  const metadata = json.data?.chartTradingMetadata || null;
  completedChartTradingMetadata.set(key, metadata);
  return metadata;
};

export {
  getChartAvailability,
  getChartBarPage,
  getChartBars,
  getChartTradingMetadata,
  searchChartAssets,
};
